import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Flex,
  SimpleGrid,
  Spinner,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import EventService from 'services/event.api';
import { Event, Guest, GuestStatistics, Business, User } from 'services/@types';
import GuestStatsCard from './components/GuestStatsCard';
import GuestListTable from './components/GuestListTable';

export default function EventDetail() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const [event, setEvent] = useState<Event | null>(null);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [stats, setStats] = useState<GuestStatistics>({});
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [eventData, guestsData, statsData] = await Promise.all([
        EventService.getEvent(id),
        EventService.getEventGuests(id, { limit: 200 }),
        EventService.getEventGuestStatistics(id),
      ]);
      setEvent(eventData);
      setGuests(guestsData.results || []);
      setStats(statsData);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load event details',
        status: 'error',
        duration: 3000,
      });
    }
    setLoading(false);
  }, [id, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <Flex
        direction="column"
        pt={{ sm: '125px', lg: '75px' }}
        align="center"
        justify="center"
        minH="50vh"
      >
        <Spinner size="xl" color="brand.500" />
      </Flex>
    );
  }

  if (!event) {
    return (
      <Flex direction="column" pt={{ sm: '125px', lg: '75px' }} align="center">
        <Text color="red.500" fontSize="lg">
          Event not found
        </Text>
      </Flex>
    );
  }

  const formatDate = (date?: Date | string) => {
    if (!date) return '-';
    return new Date(date).toLocaleString();
  };

  const businessName =
    typeof event.businessID === 'object' && event.businessID
      ? (event.businessID as Business).businessName
      : '-';

  const curatorName =
    typeof event.curator === 'object' && event.curator
      ? `${(event.curator as User).firstName || ''} ${(event.curator as User).familyName || ''}`
      : '-';

  return (
    <Flex direction="column" pt={{ sm: '125px', lg: '75px' }} gap="20px">
      {/* Event Info */}
      <Card p="30px">
        <Text color={textColor} fontSize="2xl" fontWeight="700" mb="5px">
          {event.name}
        </Text>
        <Text color="gray.500" fontSize="sm" mb="20px">
          {event.type || 'Event'} | #{event.eventNumber || '-'}
        </Text>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap="20px">
          <Box>
            <Text fontSize="sm" color="gray.500">
              Date & Time
            </Text>
            <Text fontSize="md" color={textColor}>
              {formatDate(event.dateAndTime)}
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" color="gray.500">
              End
            </Text>
            <Text fontSize="md" color={textColor}>
              {formatDate(event.dateAndTimeEnd)}
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" color="gray.500">
              Location
            </Text>
            <Text fontSize="md" color={textColor}>
              {event.location?.label || '-'}
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" color="gray.500">
              Business
            </Text>
            <Text fontSize="md" color={textColor}>
              {businessName}
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" color="gray.500">
              Curator
            </Text>
            <Text fontSize="md" color={textColor}>
              {curatorName}
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" color="gray.500">
              Expected Guests
            </Text>
            <Text fontSize="md" color={textColor}>
              {event.numberOfGuests ?? '-'}
              {event.numberOfGuestsChildren
                ? ` (+${event.numberOfGuestsChildren} children)`
                : ''}
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" color="gray.500">
              Created At
            </Text>
            <Text fontSize="md" color={textColor}>
              {formatDate(event.createdAt)}
            </Text>
          </Box>
        </SimpleGrid>

        {event.eventDetails && (
          <Box mt="20px">
            <Text fontSize="sm" color="gray.500">
              Details
            </Text>
            <Text fontSize="md" color={textColor}>
              {event.eventDetails}
            </Text>
          </Box>
        )}
      </Card>

      {/* Guest Stats */}
      <GuestStatsCard stats={stats} />

      {/* Guest List */}
      <GuestListTable guests={guests} />
    </Flex>
  );
}
